import React, {
  createContext,
  useCallback,
  useEffect,
  useReducer,
} from 'react';
import PropTypes from 'prop-types';
import jwtDecode from 'jwt-decode';

// project imports
import Loader from 'ui-component/Loader';
import axios from 'utils/axios';

// reducer & actions
import { LOGIN, LOGOUT, UPDATE_PROFILE } from 'store/actions';
import accountReducer from 'store/accountReducer';

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //

const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
  roles: null,
};

// Verify token expiration
const verifyToken = (token) => {
  if (!token) return false;
  try {
    const decoded = jwtDecode(token);
    return decoded.exp > Date.now() / 1000;
  } catch (error) {
    return false;
  }
};

// Set/clear auth header & localStorage
const setSession = (token) => {
  if (token) {
    localStorage.setItem('serviceToken', token);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    localStorage.removeItem('serviceToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(accountReducer, initialState);

  // Initialize auth: check token + fetch user + roles in parallel
  const initAuth = useCallback(async () => {
    const token = localStorage.getItem('serviceToken');

    if (!token || !verifyToken(token)) {
      dispatch({ type: LOGOUT });
      return;
    }

    setSession(token);

    try {
      const [userResponse, rolesResponse] = await Promise.all([
        axios.get('/admin/auth/me'),
        axios.get('/admin/auth/roles'),
      ]);

      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: userResponse.data.data || userResponse.data,
          roles: rolesResponse.data.data || [],
        },
      });
    } catch (error) {
      console.error('Auth initialization failed:', error);
      setSession(null);
      dispatch({ type: LOGOUT });
    }
  }, []);

  // Run once on mount
  useEffect(() => {
    initAuth();
  }, [initAuth]);

  // Login
  const login = async (email, password) => {
    try {
      const response = await axios.post('/admin/auth/login', {
        email,
        password,
      });
      const { token, data } = response.data.data;

      setSession(token.accessToken);

      const rolesResponse = await axios.get('/admin/auth/roles');

      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: data,
          roles: rolesResponse.data.data || [],
        },
      });
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  // Logout
  const logout = () => {
    setSession(null);
    dispatch({ type: LOGOUT });
  };

  // Forgot Password
  const forgotPassword = async (email) => {
    await axios.post('/admin/auth/forget-password', { email });
  };

  // Reset Password
  const resetPassword = async (password, token) => {
    setSession(token);
    await axios.post('/admin/auth/reset-password', { password });
    setSession(null);
  };

  // Update Profile
  const updateProfile = (updatedUser) => {
    dispatch({
      type: UPDATE_PROFILE,
      payload: {
        isLoggedIn: true,
        user: updatedUser,
      },
    });
  };

  // Show loader until initialized
  if (!state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider
      value={{
        ...state,
        login,
        logout,
        forgotPassword,
        resetPassword,
        updateProfile,
      }}
    >
      {children}
    </JWTContext.Provider>
  );
};

JWTProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default JWTContext;
